<template>
    <div>
        <h3>Orders</h3>
        <div class="row mb-2">
            <div class="form-group col">
                <input type="text" class="form-control" v-model="userEmail" placeholder="Email" />
            </div>
            <div class="form-group col">
                <select class="form-select" v-model="status" @change="getFirstPage">
                    <option value="">order status</option>
                    <option value="all">all</option>
                    <option value="pre_order">pre order</option>
                    <option value="new">new</option>
                    <option value="complete">complete</option>
                    <option value="owner_approved">owner approved</option>
                    <option value="cancelled_by_distirbutor">cancelled by distirbutor</option>
                    <option value="error">error</option>
                </select>
            </div>
            <div class="form-group col">
                <select class="form-select" v-model="paymentMethod" @change="getFirstPage">
                    <option value="">payment method</option>
                    <option value="all">all</option>
                    <option value="agreement">agreement</option>
                    <option value="stripe">stripe</option>
                    <option value="yoomoney">yoomoney</option>
                    <option value="trial">trial</option>
                </select>
            </div>
            <div class="form-group col">
                <select class="form-select" v-model="agreementId" @change="getFirstPage">
                    <option value="">agreements</option>
                    <option value="all">undefined</option>
                    <option v-for="agreement in agreements" :key="agreement.id" :value="agreement.id">
                        {{ agreement.name }}
                    </option>
                </select>
            </div>
            <div class="form-group col position-relative">
                <input
                    type="text"
                    class="form-control"
                    placeholder="Ship Info"
                    v-model="freeTextSearchInput"
                    @input="debouncedSearch($event.target.value)"
                    :style="freeTextSearchInput ? 'padding-right: 2rem' : ''"
                />
                <button
                    v-if="freeTextSearchInput"
                    type="button"
                    class="btn-close position-absolute"
                    style="right: 1rem; top: 50%; transform: translateY(-50%); font-size: 0.65rem;"
                    @click="clearFreeTextSearch"
                />
            </div>
        </div>

        <div v-if="isLoadingFirstPage" class="text-center py-5 text-muted">
            Загрузка...
        </div>
        <template v-else>
            <div v-if="orders.length === 0" class="text-center py-5 text-muted">
                Ничего не найдено
            </div>
            <template v-else>
                <table class="table table-bordered" :key="tableKey">
                    <order v-for="order in orders" :key="order.order.id" :order="order" />
                </table>
                <div v-if="isLoading" class="text-center py-3 text-muted">
                    Загрузка...
                </div>
            </template>
        </template>
    </div>
</template>

<script>
import myAxios from "@/services/myAxios";
import Order from "./Order.vue";
import { eventBus } from '@/main';
import debounce from "lodash/debounce";

const PAGE_SIZE = 40;
const SCROLL_THRESHOLD = 100;
const FREE_TEXT_MIN_LENGTH = 3;
const QUERY_URL = "/api/ordermanager/query-infinite";

export default {
    components: { Order },

    data() {
        return {
            orders: [],
            pageNumber: 1,
            hasMore: true,
            isLoading: false,
            isLoadingFirstPage: false,
            userEmail: null,
            agreementId: "",
            status: "",
            paymentMethod: "",
            agreements: [],
            tableKey: 1,
            freeTextSearch: null,
            freeTextSearchInput: "",
        };
    },

    computed: {
        queryFilters() {
            return {
                pageSize: PAGE_SIZE,
                userEmail: this.userEmail || null,
                agreementId: this.agreementId === "all" || this.agreementId === "" ? null : this.agreementId,
                status: this.status === "all" || this.status === "" ? null : this.status,
                paymentMethod: this.paymentMethod === "all" || this.paymentMethod === "" ? null : this.paymentMethod,
                freeTextSearch: this.freeTextSearch,
            };
        },
    },

    created() {
        this.debouncedSearch = debounce(this.setFreeTextSearch, 1000);
    },

    beforeMount() {
        this.getFirstPage();
    },

    mounted() {
        this.fetchAgreements();
        window.addEventListener("scroll", this.onScroll);
        eventBus.$on("refreshOrder", this.refreshOrder);
    },

    beforeDestroy() {
        window.removeEventListener("scroll", this.onScroll);
        eventBus.$off("refreshOrder", this.refreshOrder);
    },

    methods: {
        async fetchOrders(pageNumber) {
            const resp = await myAxios.post(QUERY_URL, {
                ...this.queryFilters,
                pageNumber,
            });
            return resp.data.orders ?? [];
        },

        async getFirstPage() {
            this.pageNumber = 1;
            this.hasMore = true;
            this.isLoadingFirstPage = true;
            try {
                const page = await this.fetchOrders(1);
                this.orders = page;
                this.hasMore = page.length >= PAGE_SIZE;
            } finally {
                this.isLoadingFirstPage = false;
            }
        },

        async getNextPage() {
            if (this.isLoading || !this.hasMore) return;
            this.isLoading = true;
            try {
                const nextPage = this.pageNumber + 1;
                const newOrders = await this.fetchOrders(nextPage);
                this.orders.push(...newOrders);
                this.pageNumber = nextPage;
                this.hasMore = newOrders.length >= PAGE_SIZE;
            } catch (error) {
                console.error("Error fetching next page:", error);
            } finally {
                this.isLoading = false;
            }
        },

        async fetchAgreements() {
            const resp = await myAxios.post("/api/agreement/query", {});
            this.agreements = resp.data;
        },

        onScroll() {
            const nearBottom =
                window.innerHeight + window.scrollY >=
                document.documentElement.scrollHeight - SCROLL_THRESHOLD;
            if (nearBottom) this.getNextPage();
        },

        clearFreeTextSearch() {
            this.freeTextSearchInput = "";
            this.freeTextSearch = null;
            this.getFirstPage();
        },

        setFreeTextSearch(value) {
            const trimmed = value?.trim();
            if (trimmed && trimmed.length >= FREE_TEXT_MIN_LENGTH) {
                this.freeTextSearch = trimmed;
                this.getFirstPage();
            } else if (!trimmed) {
                this.freeTextSearch = null;
                this.getFirstPage();
            }
        },

        async refreshOrder(orderId) {
            const resp = await myAxios.post(QUERY_URL, {
                orderId,
                pageSize: 1,
                pageNumber: 1,
            });
            const updated = resp.data.orders?.[0];
            const index = this.orders.findIndex(x => x.order.id === orderId);
            if (index !== -1 && updated) {
                this.orders[index] = updated;
                this.tableKey++;
            }
        },
    },

    watch: {
        userEmail() {
            this.getFirstPage();
        },
    },
};
</script>

<style>
table {
    font-size: 16px;
}

div.customer-info > div {
    line-height: 18px;
}
</style>
